import { Component, ComponentInterface, Element, h, Host, Method, Prop } from "@stencil/core";
import SimpleBar from "simplebar-core";

@Component({
    tag: "uic-scrollpanel",
    styleUrl: "scrollpanel.scss"
})
export class ScrollPanel implements ComponentInterface {
    @Element()
    private host: HTMLUicScrollpanelElement;
    private simplebar: SimpleBar;
    private scrollingElementPromise;
    private scrollingElementResolver;

    /**
     * If set to `true` the scrollbars are not hidden when not hovering the scroll panel
     */
    @Prop() readonly scrollbarAutoHideDisabled: boolean = false;

    // flag describing componentDidLoad
    private isLoaded = false;

    connectedCallback() {
        if (this.isLoaded) {
            // reattach simplebar
            this.simplebar = new SimpleBar(this.host);
            this.scrollingElementPromise = Promise.resolve(this.simplebar);
        } else {
            // wait for componentDidLoad
            this.scrollingElementPromise = new Promise((resolve) => {
                this.scrollingElementResolver = resolve;
            });
        }
    }

    componentDidLoad() {
        // see the storybook docs for this component to learn more about why we need
        // to set and remove "uic-scroll-init"
        const content = this.host.querySelector(".simplebar-content");
        content.setAttribute("uic-scroll-init", "");

        this.simplebar = new SimpleBar(this.host);
        this.scrollingElementResolver(this.simplebar);

        this.isLoaded = true;
        this.scrollingElementResolver = null;

        setTimeout(() => content.removeAttribute("uic-scroll-init"));
    }

    disconnectedCallback() {
        if (this.simplebar) {
            this.simplebar.unMount();
            this.simplebar = null;
        }
    }

    @Method()
    async getScrollElement() {
        // wrapped inside a promise to prevent a bug
        // where parent components do not wait for componentDidLoad
        // of this component to finish
        const simplebar = await this.scrollingElementPromise;
        // return untyped, because (as of stencil 2.3)
        // a return type of "Element" causes a build error
        return simplebar.getScrollElement();
    }

    render() {
        return (
            <Host
                class={{
                    [`uic-scrollpanel--scrollbar-auto-hide-disabled`]: this.scrollbarAutoHideDisabled
                }}
                data-simplebar
            >
                <div class='simplebar-wrapper'>
                    <div class='simplebar-height-auto-observer-wrapper'>
                        <div class='simplebar-height-auto-observer'></div>
                    </div>
                    <div class='simplebar-mask'>
                        <div class='simplebar-offset'>
                            <div class='simplebar-content-wrapper'>
                                <div class='simplebar-content'>
                                    <slot></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class='simplebar-placeholder'></div>
                </div>
                <div class='simplebar-track simplebar-horizontal'>
                    <div class='simplebar-scrollbar'></div>
                </div>
                <div class='simplebar-track simplebar-vertical'>
                    <div class='track-indicator'></div>
                    <div class='simplebar-scrollbar'></div>
                </div>
            </Host>
        );
    }
}
